import { PlusOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Empty, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useTasksList } from "@/modules/tasks/hooks/useTasks";
import { TASK_STATUS_COLORS, type Task, type TaskStatus } from "@/modules/tasks/types";
import { DataTable } from "@/shared/components/DataTable";
import { usePermission } from "@/shared/hooks/usePermission";
import { useDepartmentSummary } from "../hooks/useDepartments";

const { Text } = Typography;

/** Mahalliy vaqtda ko'rsatish — server UTC `Z` yuboradi (`TasksPage` bilan bir xil). */
function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString("uz-UZ", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Bo'lim profilining "Topshiriqlar" tabi — ikki ro'yxat: «kelgan»
 * (`assigneeDepartmentId` — ijrochilar shu bo'limdan) va «chiqqan»
 * (`departmentId` — topshiriqni shu bo'lim yaratgan), spec 2026-09-14 §4.1.
 * Holat chiplari (`summary.tasksByStatus`) «chiqqan» bo'limi ichida —
 * `tasksRepository.countByStatus` `departmentId` (yaratgan bo'lim) bo'yicha
 * sanaydi, ya'ni faqat shu bo'lim YARATGAN topshiriqlarni qamraydi.
 * `muddatiOtgan` alohida status EMAS (`faol` ichida HAM sanaladi), ya'ni
 * chiplar yig'indisi jami topshiriqlar soniga teng emas.
 *
 * ⚠️ `key={departmentId}` MAJBURIY (`DepartmentEmployeesTab` bilan bir xil
 * sabab): boshqa bo'limga o'tish AYNI marshrut, router komponentni qayta
 * mount QILMAYDI, `usePaginatedQuery` sahifasi esa ichki `useState` da —
 * kalitsiz yangi bo'lim oldingisining N-sahifasi bilan ochilardi.
 */
export function DepartmentTasksTab({
	departmentId,
	isActive = true,
}: {
	departmentId: string;
	/** Arxivlangan bo'limda «Yangi topshiriq» chizilmaydi. */
	isActive?: boolean;
}) {
	return (
		<DepartmentTasksContent key={departmentId} departmentId={departmentId} isActive={isActive} />
	);
}

function DepartmentTasksContent({
	departmentId,
	isActive,
}: {
	departmentId: string;
	isActive: boolean;
}) {
	const { t } = useTranslation("departments");
	// Holat yorlig'i `tasks.json` dan — `TasksPage` bilan yagona manba.
	const { t: tTasks } = useTranslation("tasks");
	const navigate = useNavigate();
	const { can } = usePermission();
	const { data: summary } = useDepartmentSummary(departmentId);
	// ⚠️ Ikkalasi ham `includeDescendants: true` — chiplar bo'lim + avlodlarni sanaydi.
	const kelganQuery = useTasksList({
		assigneeDepartmentId: departmentId,
		includeDescendants: true,
	});
	const chiqqanQuery = useTasksList({ departmentId, includeDescendants: true });

	const yaratish = can("tasks", "create") && isActive;

	const columns: ColumnsType<Task> = [
		{
			title: t("tasksTab.columns.name"),
			key: "name",
			render: (_, task) => (
				<Link
					to="/tasks/$taskId"
					params={{ taskId: task.id }}
					className="text-[#1c5cab] hover:underline"
				>
					{task.name}
				</Link>
			),
		},
		{
			title: t("tasksTab.columns.status"),
			dataIndex: "status",
			key: "status",
			render: (status: TaskStatus) => (
				<Tag color={TASK_STATUS_COLORS[status]}>{tTasks(`status.${status}`)}</Tag>
			),
		},
		{
			title: t("tasksTab.columns.dueTo"),
			dataIndex: "dueTo",
			key: "dueTo",
			render: (dueTo: string) => formatDateTime(dueTo),
		},
	];

	return (
		<div>
			{yaratish ? (
				<div className="mb-4 flex justify-end">
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => navigate({ to: "/tasks/new", search: { departmentId } })}
					>
						{t("tasksTab.createButton")}
					</Button>
				</div>
			) : null}

			<section aria-label={t("tasksTab.incomingTitle")}>
				<Text strong>{t("tasksTab.incomingTitle")}</Text>
				<div className="mt-2">
					{/* ⚠️ B4 (14.09.2026 fix to'lqini): `!kelganQuery.isError` HAM
					    SHART — usiz so'rov xato qaytarganda `total` `0` bo'lib
					    qoladi (`usePaginatedQuery`ning `EMPTY_META`si) va bu
					    "topshiriq yo'q" sifatida ko'rsatilardi, holbuki haqiqiy
					    sabab tarmoq/server xatosi. Xato holatida `DataTable`
					    o'z xato holatini chizadi. */}
					{kelganQuery.meta.total === 0 && !kelganQuery.isLoading && !kelganQuery.isError ? (
						<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("tasksTab.incomingEmpty")} />
					) : (
						<DataTable<Task> columns={columns} query={kelganQuery} />
					)}
				</div>
			</section>

			<section aria-label={t("tasksTab.outgoingTitle")} className="mt-6">
				<Text strong>{t("tasksTab.outgoingTitle")}</Text>
				{summary && (
					<div className="mt-2 mb-4 flex flex-wrap gap-3">
						<Tag color={TASK_STATUS_COLORS.faol}>
							{t("tasksTab.chips.faol", { count: summary.tasksByStatus.faol })}
						</Tag>
						<Tag color="error">
							{t("tasksTab.chips.muddatiOtgan", { count: summary.tasksByStatus.muddatiOtgan })}
						</Tag>
						<Tag color={TASK_STATUS_COLORS.yakunlangan}>
							{t("tasksTab.chips.yakunlangan", { count: summary.tasksByStatus.yakunlangan })}
						</Tag>
						<Tag color={TASK_STATUS_COLORS.bekor_qilingan}>
							{t("tasksTab.chips.bekorQilingan", { count: summary.tasksByStatus.bekorQilingan })}
						</Tag>
					</div>
				)}
				<div className="mt-2">
					<DataTable<Task> columns={columns} query={chiqqanQuery} />
				</div>
			</section>
		</div>
	);
}
